/**
 * Client-side PHI heuristic scanner. Deliberately conservative —
 * false positives are acceptable; the server remains the source of truth.
 *
 * Patterns:
 *  - claim:   \b[A-Z]{2,}-?\d{5,}\b   (e.g. WC-1234567, MRN-998877)
 *  - dob:     \b\d{1,2}/\d{1,2}/\d{4}\b
 *  - ssn:     \b\d{3}-\d{2}-\d{4}\b
 *  - patient: whole-word case-insensitive match of BOTH first + last name
 *  - payer:   common insurance brand mentions (Aetna, BCBS, ...)
 */

const PATTERNS = [
  { key: 'claim', label: 'claim-number-like', re: /\b[A-Z]{2,}-?\d{5,}\b/g },
  { key: 'dob', label: 'date-of-birth-like', re: /\b\d{1,2}\/\d{1,2}\/\d{4}\b/g },
  { key: 'ssn', label: 'SSN-like', re: /\b\d{3}-\d{2}-\d{4}\b/g },
  {
    key: 'payer',
    label: 'payer name',
    re: /\b(Aetna|BlueCross|BlueShield|BCBS|UnitedHealthcare|UnitedHealth|Cigna|Humana|Anthem|Kaiser|Medicare|Medicaid)\b/gi,
  },
];

const SNIPPET_CAP = 40;

const capSnippet = (text) => {
  if (!text) return '';
  return text.length > SNIPPET_CAP ? `${text.slice(0, SNIPPET_CAP - 1)}…` : text;
};

const collectMatches = (regex, text) => {
  const out = [];
  regex.lastIndex = 0;
  let m = regex.exec(text);
  while (m !== null) {
    out.push(m[0]);
    if (m.index === regex.lastIndex) regex.lastIndex += 1;
    m = regex.exec(text);
  }
  return out;
};

const scanField = (field, text, hits) => {
  if (!text) return;
  PATTERNS.forEach(({ key, label, re }) => {
    collectMatches(re, text).forEach((snippet) => {
      hits.push({ field, pattern: key, label, snippet: capSnippet(snippet) });
    });
  });
};

const scanPatientName = (field, text, firstName, lastName, hits) => {
  if (!text || !firstName || !lastName) return;
  const fn = firstName.trim();
  const ln = lastName.trim();
  if (!fn || !ln) return;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const re = new RegExp(`\\b${escape(fn)}\\b[\\s\\S]{0,30}\\b${escape(ln)}\\b`, 'i');
  const matches = collectMatches(re, text);
  if (matches.length) {
    hits.push({
      field,
      pattern: 'patient_name',
      label: 'patient name',
      snippet: capSnippet(matches[0]),
    });
  }
};

export const scanForPhi = ({ subject = '', body = '', patientFirstName = '', patientLastName = '' } = {}) => {
  const hits = [];
  scanField('subject', subject, hits);
  scanField('body', body, hits);
  scanPatientName('subject', subject, patientFirstName, patientLastName, hits);
  scanPatientName('body', body, patientFirstName, patientLastName, hits);
  return { flagged: hits.length > 0, hits };
};
